import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft,
  BadgeCheck,
  Coins,
  CreditCard,
  Layers,
  PauseCircle,
  PlayCircle,
  Plus,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  Unlock,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
  initials,
} from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/primitives";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/overlays";
import { ConfirmDialog } from "@/components/data/ConfirmDialog";
import { StatusBadge, RoleBadge } from "./shared";

const MiniStat = ({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: React.ReactNode;
  icon: React.ComponentType<{ className?: string }>;
}) => (
  <Card>
    <CardContent className="flex items-center gap-3 p-3.5">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-surface text-primary">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="numeric text-base font-semibold text-foreground">{value}</p>
      </div>
    </CardContent>
  </Card>
);

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-center justify-between py-2.5 text-sm">
    <dt className="text-muted-foreground font-medium">{label}</dt>
    <dd className="font-medium text-foreground">{children}</dd>
  </div>
);

export const UserDetailPage = () => {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [action, setAction] = React.useState<null | "ban" | "unban" | "delete">(null);
  const [creditsOpen, setCreditsOpen] = React.useState(false);
  const [creditsToAdd, setCreditsToAdd] = React.useState(5);
  const [assignSubOpen, setAssignSubOpen] = React.useState(false);
  const [selectedPlanId, setSelectedPlanId] = React.useState<string>("");

  const detail = useQuery({
    queryKey: ["users", id],
    queryFn: () => api.get<any>(`/users/${id}`),
    enabled: Boolean(id),
  });

  const { data: plansData } = useQuery({
    queryKey: ["plans"],
    queryFn: () => api.get<{ items: any[] }>("/plans"),
    enabled: assignSubOpen,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["users", id] });
    void queryClient.invalidateQueries({ queryKey: ["users"] });
    void queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
    void queryClient.invalidateQueries({ queryKey: ["dashboard", "kpis"] });
  };

  const mutate = useMutation({
    mutationFn: async ({ path, method = "POST", body }: { path: string; method?: string; body?: any }) => {
      if (method === "DELETE") return api.deleteWithMessage(path);
      return api.postWithMessage(path, body);
    },
    onSuccess: ({ message }) => {
      toast.success(message);
      setAction(null);
      if (action === "delete") {
        navigate("/users");
      } else {
        invalidate();
      }
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Action failed"),
  });

  const addCreditsMutation = useMutation({
    mutationFn: (credits: number) => api.postWithMessage(`/users/${id}/credits`, { credits }),
    onSuccess: ({ message }) => {
      toast.success(message);
      setCreditsOpen(false);
      invalidate();
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Failed to grant credits"),
  });

  const assignSubMutation = useMutation({
    mutationFn: (plan_id: string) =>
      api.postWithMessage("/subscriptions/assign", { user_id: id, plan_id }),
    onSuccess: ({ message }) => {
      toast.success(message);
      setAssignSubOpen(false);
      setSelectedPlanId("");
      invalidate();
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Failed to assign subscription"),
  });

  const cancelSubMutation = useMutation({
    mutationFn: (subId: string) => api.postWithMessage(`/subscriptions/${subId}/cancel`),
    onSuccess: ({ message }) => {
      toast.success(message);
      invalidate();
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Failed to cancel subscription"),
  });

  if (detail.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (detail.error || !detail.data) {
    return (
      <EmptyState
        icon={ShieldAlert}
        title="Could not load this user"
        description={detail.error instanceof Error ? detail.error.message : "User not found"}
        action={
          <Button variant="outline" onClick={() => navigate("/users")}>
            Back to users
          </Button>
        }
      />
    );
  }

  const u = detail.data;
  const isBanned = u.status === "rejected" || u.status === "banned";
  const plans = plansData?.items ?? [];

  return (
    <div className="space-y-5">
      {/* Back button */}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon-sm" asChild>
          <Link to="/users" aria-label="Back to users">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <p className="text-sm font-medium text-muted-foreground">User Management / Details</p>
      </div>

      {/* User Header Profile Card */}
      <Card>
        <CardContent className="flex flex-wrap items-start justify-between gap-4 p-6">
          <div className="flex items-start gap-4">
            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-primary/10 text-lg font-bold text-primary">
              {initials(u.name)}
            </span>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-bold tracking-tight text-foreground">{u.name ?? "Unnamed user"}</h1>
                <RoleBadge role={u.role} />
                <StatusBadge status={u.status} />
                {u.breeder?.verified && (
                  <Badge variant="success" className="gap-1">
                    <BadgeCheck className="h-3 w-3" /> Verified Breeder
                  </Badge>
                )}
                {!u.is_onboarded && <Badge variant="warning">Onboarding Pending</Badge>}
              </div>

              <p className="mt-1 text-sm text-muted-foreground">
                {[u.phone ? `+91 ${u.phone}` : null, u.email, u.city].filter(Boolean).join(" · ") || "—"}
              </p>

              <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                <span>Joined {formatDate(u.created_at)}</span>
                <span>Free Unlock Credits: <strong className="text-foreground">{u.free_unlock_credits}</strong></span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCreditsOpen(true)}
              className="gap-1.5 font-medium"
            >
              <Coins className="h-4 w-4 text-warning" /> Adjust Credits
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setAssignSubOpen(true)}
              className="gap-1.5 font-medium"
            >
              <Layers className="h-4 w-4 text-primary" /> Assign Plan
            </Button>

            {isBanned ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAction("unban")}
                className="gap-1.5"
              >
                <PlayCircle className="h-4 w-4 text-success" /> Lift Ban / Unsuspend
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAction("ban")}
                className="gap-1.5 text-warning hover:text-warning"
              >
                <PauseCircle className="h-4 w-4" /> Ban / Suspend
              </Button>
            )}

            <Button
              variant="destructive"
              size="sm"
              onClick={() => setAction("delete")}
              className="gap-1.5"
            >
              <Trash2 className="h-4 w-4" /> Delete Account
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Mini Stats Summary */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MiniStat
          label="Unlocked Contacts"
          value={formatNumber(u.unlocked_contacts?.length || 0)}
          icon={Unlock}
        />
        <MiniStat
          label="Subscriptions"
          value={formatNumber(u.subscriptions?.length || 0)}
          icon={Layers}
        />
        <MiniStat
          label="Total Payments"
          value={formatNumber(u.payments?.length || 0)}
          icon={CreditCard}
        />
        <MiniStat
          label="Reviews Written"
          value={formatNumber(u.reviews?.length || 0)}
          icon={ShieldCheck}
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="unlocks">Unlocked Contacts ({u.unlocked_contacts?.length || 0})</TabsTrigger>
          <TabsTrigger value="subscriptions">Subscriptions &amp; Payments</TabsTrigger>
          {u.breeder && <TabsTrigger value="breeder">Breeder Profile</TabsTrigger>}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Account Information</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="divide-y divide-border">
                  <Row label="User ID"><code className="text-xs font-mono">{u.id}</code></Row>
                  <Row label="Mobile Number">{u.phone ? `+91 ${u.phone}` : "—"}</Row>
                  <Row label="Email Address">{u.email ?? "—"}</Row>
                  <Row label="Role / Category"><span className="capitalize">{u.role}</span></Row>
                  <Row label="Location / City">{u.city ?? "—"}</Row>
                  <Row label="Onboarded Status">{u.is_onboarded ? "Completed" : "Incomplete"}</Row>
                  <Row label="Unlock Credits">{u.free_unlock_credits}</Row>
                </dl>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Activity Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="divide-y divide-border">
                  <Row label="Registered On">{formatDateTime(u.created_at)}</Row>
                  <Row label="Last Activity">{formatDateTime(u.last_seen_at)}</Row>
                  <Row label="Contacts Unlocked">{u.unlocked_contacts?.length || 0}</Row>
                  <Row label="Active Subscriptions">{u.subscriptions?.filter((s: any) => s.status === "active").length || 0}</Row>
                  <Row label="Reports Filed">{u.reports?.length || 0}</Row>
                </dl>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Unlocks Tab */}
        <TabsContent value="unlocks">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Unlocked Breeder Contacts</CardTitle>
              <CardDescription>
                Breeder contact details unlocked by this user.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(!u.unlocked_contacts || u.unlocked_contacts.length === 0) ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  This user has not unlocked any breeder contacts yet.
                </p>
              ) : (
                <ul className="divide-y divide-border">
                  {u.unlocked_contacts.map((item: any) => (
                    <li key={item.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                      <div>
                        <p className="font-semibold text-foreground">{item.breeder?.name || "Breeder"}</p>
                        <p className="text-xs text-muted-foreground">
                          City: {item.breeder?.city || "—"} · Via: <span className="capitalize">{item.unlocked_via?.replace(/_/g, " ")}</span>
                        </p>
                      </div>
                      <div className="text-right text-xs text-muted-foreground">
                        <span className="block font-medium text-foreground">{formatDate(item.unlocked_at)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscriptions & Payments Tab */}
        <TabsContent value="subscriptions">
          <div className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div>
                  <CardTitle className="text-base">Subscriptions</CardTitle>
                  <CardDescription>Active and past membership plans for this account.</CardDescription>
                </div>
                <Button size="sm" onClick={() => setAssignSubOpen(true)} className="gap-1.5 font-medium">
                  <Plus className="h-4 w-4" /> Assign Plan
                </Button>
              </CardHeader>
              <CardContent>
                {(!u.subscriptions || u.subscriptions.length === 0) ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    No active or historical subscriptions.
                  </p>
                ) : (
                  <ul className="divide-y divide-border">
                    {u.subscriptions.map((sub: any) => (
                      <li key={sub.id} className="flex items-center justify-between py-3">
                        <div>
                          <p className="font-semibold text-foreground">{sub.plan?.name || "Subscription Plan"}</p>
                          <p className="text-xs text-muted-foreground">
                            Expires: {formatDate(sub.end_date)} · Remaining Unlocks: <strong className="text-foreground">{sub.remaining_unlocks}</strong>
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={sub.status === "active" ? "success" : "secondary"}>
                            {sub.status}
                          </Badge>
                          {sub.status === "active" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => cancelSubMutation.mutate(sub.id)}
                              loading={cancelSubMutation.isPending}
                              className="text-xs text-destructive hover:text-destructive h-7 px-2"
                            >
                              Cancel
                            </Button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Payment Transactions</CardTitle>
              </CardHeader>
              <CardContent>
                {(!u.payments || u.payments.length === 0) ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    No payment history recorded.
                  </p>
                ) : (
                  <ul className="divide-y divide-border">
                    {u.payments.map((p: any) => (
                      <li key={p.id} className="flex items-center justify-between py-3">
                        <div>
                          <p className="font-semibold text-foreground">{formatMoney(Number(p.amount))}</p>
                          <p className="text-xs text-muted-foreground">
                            Order ID: {p.razorpay_order_id || p.id}
                          </p>
                        </div>
                        <Badge variant={p.status === "success" ? "success" : "secondary"}>
                          {p.status}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Breeder Profile Tab */}
        {u.breeder && (
          <TabsContent value="breeder">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Breeder Business Profile</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <dl className="divide-y divide-border">
                  <Row label="Shop Name">{u.breeder.name}</Row>
                  <Row label="Status">
                    <Badge variant={u.breeder.status === "approved" ? "success" : "secondary"}>
                      {u.breeder.status}
                    </Badge>
                  </Row>
                  <Row label="Verified Badge">{u.breeder.verified ? "Yes" : "No"}</Row>
                  <Row label="Pet Shop Registered">{u.breeder.has_pet_shop ? "Yes" : "No"}</Row>
                  <Row label="Rating">{u.breeder.avg_rating} ★ ({u.breeder.review_count} reviews)</Row>
                </dl>
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/breeders/${u.breeder.id}`}>
                    View Full Breeder Profile &amp; Documents &rarr;
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Adjust Credits Modal */}
      <Dialog open={creditsOpen} onOpenChange={setCreditsOpen}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Adjust Free Unlock Credits</DialogTitle>
            <DialogDescription>
              Grant additional free breeder contact unlocks to this user.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="text-xs font-medium text-muted-foreground block">
              Credits to Add (Current balance: {u.free_unlock_credits})
            </label>
            <Input
              type="number"
              value={creditsToAdd}
              onChange={(e) => setCreditsToAdd(Number(e.target.value))}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditsOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => addCreditsMutation.mutate(creditsToAdd)}
              loading={addCreditsMutation.isPending}
            >
              Add Credits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Plan Modal */}
      <Dialog open={assignSubOpen} onOpenChange={setAssignSubOpen}>
        <DialogContent size="default">
          <DialogHeader>
            <DialogTitle>Assign Subscription Plan</DialogTitle>
            <DialogDescription>
              Select a membership plan to assign to this account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="text-xs font-medium text-muted-foreground block">
              Select Plan
            </label>
            <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a plan..." />
              </SelectTrigger>
              <SelectContent>
                {plans.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name} — {formatMoney(p.price)} ({p.validity_days} days, {p.contact_unlocks} unlocks)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignSubOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!selectedPlanId}
              onClick={() => assignSubMutation.mutate(selectedPlanId)}
              loading={assignSubMutation.isPending}
            >
              Assign Subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialogs */}
      <ConfirmDialog
        open={action === "ban"}
        onOpenChange={(open) => !open && setAction(null)}
        title="Ban / Suspend this User?"
        description="Banning this user will restrict their access and mark any linked breeder profile as rejected."
        confirmLabel="Ban User"
        variant="destructive"
        loading={mutate.isPending}
        onConfirm={() => mutate.mutate({ path: `/users/${id}/ban`, body: { reason: "Banned by administrator" } })}
      />

      <ConfirmDialog
        open={action === "unban"}
        onOpenChange={(open) => !open && setAction(null)}
        title="Lift User Ban / Suspension?"
        description="This will restore the user's active status."
        confirmLabel="Restore User"
        variant="default"
        loading={mutate.isPending}
        onConfirm={() => mutate.mutate({ path: `/users/${id}/unban` })}
      />

      <ConfirmDialog
        open={action === "delete"}
        onOpenChange={(open) => !open && setAction(null)}
        title="Permanently Delete User Account?"
        description="This action cannot be undone. All data associated with this user will be removed."
        confirmLabel="Delete User"
        variant="destructive"
        loading={mutate.isPending}
        onConfirm={() => mutate.mutate({ path: `/users/${id}`, method: "DELETE" })}
      />
    </div>
  );
};
